import type { EmeterMainMetric } from 'src/helpers/emeter_main_metric';
import {
	readDevicePhaseValues,
	type EmeterPhaseMetric,
} from 'src/helpers/emeter_phase_values';

export type DevicePhaseBar = {
	phase: string;
	displayName: string;
	value: number;
	label: string;
	percent: number;
	tone: 'primary' | 'success' | 'warning';
	voltage: number | null;
	otherMetricValue: number | null;
	otherMetricLabel: string;
	aprtPower: number | null;
	pf: number | null;
	freq: number | null;
	totalActEnergy: number | null;
	totalActRetEnergy: number | null;
};

export type DevicePhaseHealth = {
	phases: DevicePhaseBar[];
	totalValue: number;
	totalLabel: string;
	imbalancePercent: number;
	isBalanced: boolean;
	nCurrent: number | null;
	hasNeutral: boolean;
};

export type EmeterHeroReading = {
	value: number | null;
	display: string;
	unit: string;
	hasValue: boolean;
};

const PHASES = ['a', 'b', 'c'] as const;

const toMetric = (metric: EmeterMainMetric): EmeterPhaseMetric => metric;

/** Lightweight W/A formatter that avoids the heavy utilities import graph in unit tests. */
const formatMetricValue = (value: number, unit: string): { value: string; unit: string } => {
	let next = Math.abs(value);
	let nextUnit = unit;
	if (next > 999) {
		next = next / 1000;
		nextUnit = `k${unit}`;
	}
	const rounded = Math.round(next * 100) / 100;
	return { value: String(rounded), unit: nextUnit };
};

const formatMetric = (value: number, unit: string): string => {
	const formatted = formatMetricValue(value, unit);
	return `${formatted.value} ${formatted.unit}`;
};

/**
 * Builds a device-scoped phase overview for the Sidepanel Main tab.
 * Supports power or current as the primary bar metric.
 */
export const buildDevicePhaseHealth = (
	status: Record<string, unknown> | undefined,
	metric: EmeterMainMetric,
	channelNames?: Array<string | undefined>,
): DevicePhaseHealth => {
	const empty: DevicePhaseHealth = {
		phases: [],
		totalValue: 0,
		totalLabel: '—',
		imbalancePercent: 0,
		isBalanced: true,
		nCurrent: null,
		hasNeutral: false,
	};

	const values = readDevicePhaseValues(status, toMetric(metric));
	if (!values) {
		return empty;
	}

	const absValues = values.map((value) => Math.abs(value));
	const max = Math.max(...absValues, 1);
	const min = Math.min(...absValues);
	const imbalancePercent = max > 0 ? Math.round(((max - min) / max) * 100) : 0;
	const isBalanced = imbalancePercent <= 15;
	const totalValue = absValues.reduce((sum, value) => sum + value, 0);
	const unit = metric === 'power' ? 'W' : 'A';
	const otherUnit = metric === 'power' ? 'A' : 'W';
	const totalFormatted = formatMetricValue(totalValue, unit);

	const emKey = status ? Object.keys(status).find((key) => /^em:\d+$/.test(key)) : undefined;
	const em = (emKey && status ? (status[emKey] as Record<string, unknown>) : {}) || {};
	const emDataKey = status
		? Object.keys(status).find((key) => /^emdata:\d+$/.test(key))
		: undefined;
	const emData =
		(emDataKey && status ? (status[emDataKey] as Record<string, unknown>) : {}) || {};

	const otherValues = readDevicePhaseValues(status, metric === 'power' ? 'current' : 'power');

	const phases = PHASES.map((phase, index) => {
		const value = absValues[index];
		const isHottest = !isBalanced && value === max && max > 0;
		const voltageRaw = em[`${phase}_voltage`];
		const otherRaw = otherValues?.[index];
		const aprtRaw = em[`${phase}_aprt_power`];
		const pfRaw = em[`${phase}_pf`];
		const freqRaw = em[`${phase}_freq`];
		const totalActRaw = emData[`${phase}_total_act_energy`] ?? em[`${phase}_total_act_energy`];
		const totalRetRaw =
			emData[`${phase}_total_act_ret_energy`] ?? em[`${phase}_total_act_ret_energy`];

		return {
			phase: phase.toUpperCase(),
			displayName: channelNames?.[index] || `Phase ${phase.toUpperCase()}`,
			value,
			label: value > 0 ? formatMetric(value, unit) : '—',
			percent: Math.round((value / max) * 100),
			tone: (isHottest ? 'warning' : value > 0 ? 'primary' : 'success') as DevicePhaseBar['tone'],
			voltage: typeof voltageRaw === 'number' ? voltageRaw : null,
			otherMetricValue: typeof otherRaw === 'number' ? Math.abs(otherRaw) : null,
			otherMetricLabel:
				typeof otherRaw === 'number' && Math.abs(otherRaw) > 0
					? formatMetric(otherRaw, otherUnit)
					: '—',
			aprtPower: typeof aprtRaw === 'number' ? aprtRaw : null,
			pf: typeof pfRaw === 'number' ? pfRaw : null,
			freq: typeof freqRaw === 'number' ? freqRaw : null,
			totalActEnergy: typeof totalActRaw === 'number' ? totalActRaw : null,
			totalActRetEnergy: typeof totalRetRaw === 'number' ? totalRetRaw : null,
		};
	});

	const calibrated = Array.isArray(em.user_calibrated_phase)
		? (em.user_calibrated_phase as string[])
		: [];
	const hasNeutral = calibrated.includes('n') && typeof em.n_current === 'number';
	const nCurrent = hasNeutral ? Number(em.n_current) : null;

	return {
		phases,
		totalValue,
		totalLabel: totalValue > 0 ? `${totalFormatted.value} ${totalFormatted.unit}` : '—',
		imbalancePercent,
		isBalanced,
		nCurrent,
		hasNeutral,
	};
};

/**
 * Resolves the hero live reading for Gen2+ emeters (triphase or single-channel).
 */
export const getEmeterHeroReading = (
	status: Record<string, unknown> | undefined,
	device: { channel?: number; mode?: string } | undefined,
	metric: EmeterMainMetric,
	isSingleChannel: boolean,
): EmeterHeroReading => {
	const empty: EmeterHeroReading = {
		value: null,
		display: '—',
		unit: metric === 'power' ? 'W' : 'A',
		hasValue: false,
	};

	if (!status || device == null) {
		return empty;
	}

	const channel = device.channel ?? 0;
	let raw: number | null = null;

	if (isSingleChannel) {
		const em1 = status[`em1:${channel}`] as Record<string, number> | undefined;
		raw =
			metric === 'power'
				? (em1?.act_power ?? null)
				: (em1?.current ?? null);
	} else {
		const em = status[`em:${channel}`] as Record<string, number> | undefined;
		raw =
			metric === 'power'
				? (em?.total_act_power ?? null)
				: (em?.total_current ?? null);
	}

	if (raw == null || Number.isNaN(Number(raw))) {
		return empty;
	}

	const unit = metric === 'power' ? 'W' : 'A';
	const formatted = formatMetricValue(Number(raw), unit);
	return {
		value: Number(raw),
		display: `${formatted.value} ${formatted.unit}`,
		unit: formatted.unit,
		hasValue: true,
	};
};

/**
 * Whether the Current lens can be offered for this device status.
 */
export const hasEmeterCurrentReading = (
	status: Record<string, unknown> | undefined,
	device: { channel?: number } | undefined,
	isSingleChannel: boolean,
): boolean => {
	if (!status || device == null) {
		return false;
	}

	const channel = device.channel ?? 0;
	if (isSingleChannel) {
		const em1 = status[`em1:${channel}`] as Record<string, unknown> | undefined;
		return typeof em1?.current === 'number';
	}

	const em = status[`em:${channel}`] as Record<string, unknown> | undefined;
	return typeof em?.total_current === 'number' || typeof em?.a_current === 'number';
};

/**
 * Label names that the Sidepanel Main content already shows (hero, bars, cards).
 * Used to keep DeviceLabelList from duplicating those metrics.
 */
export const getEmeterMainLabelExcludeNames = (
	device: { type?: string; mode?: string; channel?: number; category?: string } | undefined,
	status: Record<string, unknown> | undefined,
	metric: EmeterMainMetric,
): string[] => {
	if (!device || !status) {
		return [];
	}

	const exclude = new Set<string>([
		'power',
		'current',
		'voltage',
		'aprt_power',
		'pf',
		'freq',
		'total_act',
		'total_act_ret',
		'active_tariff',
	]);

	if (metric === 'power') {
		exclude.add('power');
	} else {
		exclude.add('current');
	}

	const phases = ['a', 'b', 'c'];
	const perPhase = [
		'act_power',
		'aprt_power',
		'current',
		'voltage',
		'pf',
		'freq',
		'total_act',
		'total_act_ret',
	];
	phases.forEach((phase) => {
		perPhase.forEach((field) => {
			exclude.add(`${phase}_${field}`);
		});
	});

	return [...exclude];
};
